import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Stack,
  Checkbox, MenuItem, FormControlLabel, Alert, Divider
} from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { getFormById, getMyFormResponse, submitFormResponse } from '../controllers/form.controller';
import { getMe } from '../controllers/auth.controller';
import { ReactSketchCanvas } from 'react-sketch-canvas';


const FormResponsePage = () => {
  const autoSaveTimer = useRef(null);

  const { formId } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const subjectId = query.get('subjectId');
  const [form, setForm] = useState(null);
  const [user, setUser] = useState(null);
  const [answers, setAnswers] = useState({});
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps, setTotalSteps] = useState(1);

  useEffect(() => {
    const init = async () => {
      try {
        const me = await getMe();
        setUser(me);

        const form = await getFormById(formId);
        setForm(form);

        // console.table(form.structure.map(f => ({
        //   fieldId: f.fieldId,
        //   title: f.titlep
        // })));

        // محاسبه مراحل
        const steps = [...new Set(form.structure.map(f => f.stepNumber || 1))];
        setTotalSteps(steps.length);

        // بررسی تخصیص
        if (!form.assignedTo.includes(me._id)) {
          setBlocked(true);
          toast.error('⛔ شما به این فرم دسترسی ندارید.');
          return;
        }

        // دریافت پاسخ قبلی (اگر وجود دارد)
        const response = await getMyFormResponse(formId, subjectId);
        console.log('response', response);

        if (response && response.answers) {
          const existingAnswers = {};
          response.answers.forEach(({ fieldId, value }) => {
            existingAnswers[fieldId] = value;
          });
          setAnswers(existingAnswers);

          const status = response.evaluation?.status;
          if (status === 'accepted') {
            setBlocked(true);
            toast.info('✅ پاسخ شما قبلاً تأیید شده است و فقط قابل مشاهده می‌باشد.');
          } else if (status === 'rejected') {
            toast.warning('⚠️ پاسخ قبلی شما رد شده است. لطفاً پاسخ جدید وارد کنید.');
          } else {
            toast.info('📝 پاسخ قبلی شما بارگذاری شد و قابل ویرایش است.');
          }
        }
      } catch (err) {
        console.error(err);
        toast.error('❌ خطا در بارگذاری فرم یا اطلاعات کاربر.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [formId, subjectId]);

  const handleSubmit = async () => {
    try {
      if (!form || !user) {
        toast.error('اطلاعات فرم یا کاربر ناقص است.');
        return;
      }

      // اعتبارسنجی فیلدهای الزامی فقط در مرحله فعلی
      const visibleFields = form.structure.filter(f => (f.stepNumber || 1) === currentStep);
      for (let field of visibleFields) {
        const val = answers[field.fieldId];
        const isEmpty = val === undefined || val === '' || (Array.isArray(val) && val.length === 0);
        if (field.required && isEmpty) {
          toast.warning(`فیلد "${field.titlep}" را تکمیل کنید.`);
          return;
        }

        if (field.type === 'number' && !isEmpty) {
          const value = parseFloat(val);
          const min = field.validation?.min;
          const max = field.validation?.max;
          if (min != null && value < min) {
            toast.warning(`مقدار "${field.titlep}" نباید کمتر از ${min} باشد.`);
            return;
          }
          if (max != null && value > max) {
            toast.warning(`مقدار "${field.titlep}" نباید بیشتر از ${max} باشد.`);
            return;
          }
        }

        // if (field.type === 'rating' && !isEmpty) {
        //   const value = parseInt(val);
        //   if (field.min != null && value < field.min) {
        //     toast.warning(`امتیاز "${field.titlep}" نباید کمتر از ${field.min} باشد.`);
        //     return;
        //   }
        //   if (field.max != null && value > field.max) {
        //     toast.warning(`امتیاز "${field.titlep}" نباید بیشتر از ${field.max} باشد.`);
        //     return;
        //   }
        // }

      }

      // ساخت FormData برای ارسال فایل‌ها
      const formData = new FormData();
      formData.append('userId', user._id);
      formData.append('formId', formId);
      formData.append('step', currentStep);
      formData.append('isFinal', currentStep === totalSteps);
      if (subjectId) {
        formData.append('subjectId', subjectId);
      }

      const answersArray = [];

      for (let [fieldId, value] of Object.entries(answers)) {
        if (value instanceof File) {
          const fileKey = `file-${fieldId}`;
          formData.append(fileKey, value); // فایل واقعی
          answersArray.push({ fieldId, value: fileKey }); // اسم کلید فایل در پاسخ
        } else {
          answersArray.push({ fieldId, value });
        }
      }

      formData.append('answers', JSON.stringify(answersArray));

      await submitFormResponse(formData, true); // ← پارامتر دوم true یعنی multipart
      toast.success('✅ پاسخ شما با موفقیت ثبت شد.');
    } catch (err) {
      console.error('خطا در ارسال پاسخ:', err);
      toast.error(err.response?.data?.message || '❌ ارسال پاسخ با خطا مواجه شد.');
    }
  };

  const autoSaveAnswers = () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      try {
        const formData = new FormData();
        formData.append('userId', user._id);
        formData.append('formId', formId);
        formData.append('step', currentStep);
        formData.append('isFinal', false); // ذخیره موقت
        if (subjectId) {
          formData.append('subjectId', subjectId);
        }
        const answersArray = [];

        for (let [fieldId, value] of Object.entries(answers)) {
          if (value instanceof File) {
            const fileKey = `file-${fieldId}`;
            formData.append(fileKey, value);
            answersArray.push({ fieldId, value: fileKey });
          } else {
            answersArray.push({ fieldId, value });
          }
        }

        formData.append('answers', JSON.stringify(answersArray));
        await submitFormResponse(formData, true);

        console.log(`✅ پاسخ موقت ذخیره شد - مرحله ${currentStep}`);
      } catch (err) {
        console.warn('⛔ Auto-save خطا داشت', err.message);
      }
    }, 5000); // 5 ثانیه دیبونس
  };


  if (loading) return <Typography>در حال بارگذاری...</Typography>;

  if (blocked) {
    return (
      <Box sx={{ p: 3 }}>
        <ToastContainer position="top-right" />
        <Typography variant="h6" fontWeight="bold" mb={2}>پاسخ ثبت‌شده شما</Typography>
        {form.structure.map((field, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Typography fontWeight="bold" mb={1}>{field.titlep}</Typography>

            {field.type === 'file' && answers[field.fieldId] ? (
              <a
                href={`/uploads/${answers[field.fieldId]}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1565c0', textDecoration: 'underline' }}
              >
                📎 دانلود فایل
              </a>
            ) : (
              <Typography>
                {Array.isArray(answers[field.fieldId])
                  ? answers[field.fieldId].join('، ')
                  : answers[field.fieldId] || '—'}
              </Typography>
            )}
          </Box>
        ))}

        <Alert severity="info" sx={{ mt: 3 }}>
          این پاسخ تأیید شده است و قابل ویرایش نمی‌باشد.
        </Alert>
      </Box>
    );
  }



  return (
    <Box sx={{ p: 3, direction: 'rtl' }}>
      <ToastContainer position="top-right" />
      <Typography variant="h5" fontWeight="bold" mb={3}>
        📝 پاسخ به فرم: {form?.title?.fa || 'فرم بدون عنوان'} (مرحله {currentStep} از {totalSteps})
      </Typography>

      {/* دسته‌بندی بر اساس گروه */}
      {Object.entries(
        form.structure
          .filter(f => (f.stepNumber || 1) === currentStep)
          .filter(f => {
            if (!f.visibilityCondition) return true;

            const { parentFieldId, expectedValue } = f.visibilityCondition;
            const parentValue = answers[parentFieldId];

            // اگر هنوز پاسخی برای سوال والد ثبت نشده، سوال فرزند را نشان نده
            if (parentValue == null) return false;

            // اگر سوال والد چندگزینه‌ای باشد، مقدار جواب یک آرایه است
            if (Array.isArray(parentValue)) {
              // نمایش اگر یکی از گزینه‌های تیک‌خورده برابر expectedValue باشد
              return parentValue.includes(expectedValue);
            }

            // در بقیه‌ی انواع (select, text, number, ...) همان مقایسه‌ی ساده
            return parentValue === expectedValue;
          })

          .reduce((acc, f) => {
            const group = f.group?.trim() || 'بدون گروه';
            if (!acc[group]) acc[group] = [];
            acc[group].push(f);
            return acc;
          }, {})
      ).map(([groupName, groupFields], i) => (
        <Box key={i} sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              backgroundColor: '#e3f2fd',
              color: '#1565c0',
              py: 1,
              px: 2,
              borderRight: '6px solid #64b5f6',
              borderRadius: 1,
              mb: 2,
              textAlign: 'right'
            }}
          >
            📁 گروه: {groupName}
          </Typography>

          {groupFields.map((field, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography fontWeight="bold"
                sx={{
                  fontSize: field.fontSize,
                  fontWeight: field.style?.cssClass === 'form-bold' ? 'bold' : 'normal',
                  fontStyle: field.style?.cssClass === 'form-italic' ? 'italic' : 'normal',
                  direction: field.style?.cssClass === 'form-rtl' ? 'rtl' : 'ltr',
                  borderLeft: `4px solid ${field.backgroundColor || '#ccc'}`,
                  pl: 1, mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {field.titlep || 'بدون عنوان'}
                {field.required && <span style={{ color: 'red' }}> *</span>}
                {field.tooltip && (
                  <span
                    title={field.tooltip}
                    style={{ color: '#888', cursor: 'help', fontSize: '18px' }}
                  >
                    ❔
                  </span>
                )}
              </Typography>


              {field.type === 'text' && (
                <TextField
                  fullWidth
                  size="small"
                  value={answers[field.fieldId] || ''}
                  onChange={(e) => {
                    setAnswers((prev) => {
                      const updated = { ...prev, [field.fieldId]: e.target.value };
                      autoSaveAnswers();
                      return updated;
                    });
                  }}

                  inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
                  placeholder="متن پاسخ"
                />
              )}

              {field.type === 'number' && (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={answers[field.fieldId] || ''}
                  onChange={(e) => {
                    setAnswers((prev) => {
                      const updated = { ...prev, [field.fieldId]: e.target.value };
                      autoSaveAnswers();
                      return updated;
                    });
                  }}

                  inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
                  placeholder={`عدد (${field.validation?.min ?? 0} تا ${field.validation?.max ?? '∞'})`}
                />
              )}

              {field.type === 'select' && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={answers[field.fieldId] || ''}
                  onChange={(e) => {
                    setAnswers((prev) => {
                      const updated = { ...prev, [field.fieldId]: e.target.value };
                      autoSaveAnswers();
                      return updated;
                    });
                  }}

                  inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
                >
                  {field.options?.map((opt, i) => (
                    <MenuItem key={i} value={opt} sx={{ direction: 'rtl', textAlign: 'right' }}>{opt}</MenuItem>
                  ))}
                </TextField>
              )}

              {field.type === 'checkbox' && (
                <Stack direction="row" spacing={2}>
                  {field.options?.map((opt, i) => (
                    <FormControlLabel
                      key={i}
                      control={
                        <Checkbox
                          checked={(answers[field.fieldId] || []).includes(opt)}
                          onChange={(e) => {
                            const current = answers[field.fieldId] || [];
                            const updatedValues = e.target.checked
                              ? [...current, opt]
                              : current.filter(o => o !== opt);
                            setAnswers((prev) => {
                              const updated = { ...prev, [field.fieldId]: updatedValues };
                              autoSaveAnswers();
                              return updated;
                            });
                          }}
                          inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
                        />
                      }
                      label={opt}
                    />
                  ))}
                </Stack>
              )}

              {field.type === 'file' && (
                <input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setAnswers((prev) => {
                        const updated = { ...prev, [field.fieldId]: file };
                        autoSaveAnswers();
                        return updated;
                      });
                    }
                  }}
                />
              )}

              {field.type === 'persian_date' && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="تاریخ شمسی (مثلاً ۱۴۰۳/۰۲/۲۹)"
                  value={answers[field.fieldId] || ''}
                  onChange={(e) => {
                    setAnswers((prev) => {
                      const updated = { ...prev, [field.fieldId]: e.target.value };
                      autoSaveAnswers();
                      return updated;
                    });
                  }}

                />
              )}
              {field.type === 'rating' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {[...Array(field.maxStars || 5)].map((_, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: '28px',
                        cursor: 'pointer',
                        color: (answers[field.fieldId] || 0) > i ? '#fbc02d' : '#e0e0e0'
                      }}
                      onClick={() => setAnswers((prev) => ({ ...prev, [field.fieldId]: i + 1 }))}
                    >
                      ★
                    </span>
                  ))}
                  <Typography variant="body2" color="textSecondary">
                    {answers[field.fieldId] ? `${answers[field.fieldId]} از ${field.maxStars || 5}` : 'امتیاز بده'}
                  </Typography>
                </Box>
              )}

              {field.type === 'drawing' && (
                <Box sx={{ border: '1px solid #ccc', width: '100%', height: 300, mb: 2 }}>
                  <ReactSketchCanvas
                    style={{ width: '100%', height: '100%' }}
                    strokeColor={field.penColor || '#000000'}
                    strokeWidth={field.penSize || 3}
                    backgroundImage={field.backgroundImage || ''}
                    onChange={(e) => {
                      setAnswers((prev) => {
                        const updated = { ...prev, [field.fieldId]: e.target.value };
                        autoSaveAnswers();
                        return updated;
                      });
                    }}

                  />
                  <Typography variant="caption" color="text.secondary">
                    🖌️ نقاشی شما در این بوم ذخیره خواهد شد.
                  </Typography>
                </Box>
              )}
              {field.type === 'matrix' && (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ccc', padding: '4px' }}></th>
                        {field.matrixCols?.map((col, i) => (
                          <th key={i} style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {field.matrixRows?.map((row, i) => (
                        <tr key={i}>
                          <td style={{ border: '1px solid #ccc', padding: '4px' }}>{row}</td>
                          {field.matrixCols?.map((col, j) => (
                            <td key={j} style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                              <input
                                type="radio"
                                name={`${field.fieldId}-${i}`} // برای هر ردیف یک گروه
                                checked={(answers[field.fieldId]?.[row] || '') === col}
                                onChange={() => {
                                  const current = answers[field.fieldId] || {};
                                  setAnswers((prev) => ({
                                    ...prev,
                                    [field.fieldId]: { ...current, [row]: col }
                                  }));
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
              {field.type === 'calculated' && (
                <Typography variant="h6" color="primary">
                  {(() => {
                    const targets = field.calculation?.targetFields || [];
                    const values = targets.map(fid => parseFloat(answers[fid]) || 0);
                    if (field.calculation?.operation === 'sum') {
                      return `جمع: ${values.reduce((a, b) => a + b, 0)}`;
                    } else if (field.calculation?.operation === 'avg') {
                      return values.length ? `میانگین: ${(values.reduce((a, b) => a + b, 0) / values.length).toFixed(2)}` : 'میانگین: 0';
                    } else {
                      return 'فرمول تعریف نشده';
                    }
                  })()}
                </Typography>
              )}

            </Box>
          ))}
        </Box>
      ))}

      <Stack direction="row" spacing={2}>
        {currentStep > 1 && (
          <Button variant="outlined" onClick={() => setCurrentStep(s => s - 1)}>
            مرحله قبل
          </Button>
        )}
        {currentStep < totalSteps ? (
          <Button variant="contained" onClick={() => setCurrentStep(s => s + 1)}>
            مرحله بعد
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            📤 ارسال نهایی
          </Button>
        )}
      </Stack>
    </Box>
  );

};

export default FormResponsePage;
